import React from 'react';

export const Card = ({ children, style = {} }) => (
  <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(15,23,42,0.04)', ...style }}>
    {children}
  </div>
);

export const StatCard = ({ icon, label, value, sub, color = '#EF1D28', trend }) => (
  <Card>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ color: '#64748B', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{label}</div>
        <div style={{ color: '#0F172A', fontSize: 28, fontWeight: 800 }}>{value}</div>
        {sub && <div style={{ color: '#64748B', fontSize: 12, marginTop: 4 }}>{sub}</div>}
        {trend !== undefined && (
          <div style={{ fontSize: 11, marginTop: 6, color: trend >= 0 ? '#15803D' : '#DC2626', fontWeight: 600 }}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)} this week
          </div>
        )}
      </div>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
        {icon}
      </div>
    </div>
  </Card>
);

export const Badge = ({ children, color = 'blue' }) => {
  const colors = {
    blue:   { bg: '#FFF1E8', text: '#C5161D', border: '#FDBA74' },
    green:  { bg: '#DCFCE7', text: '#15803D', border: '#BBF7D0' },
    red:    { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA' },
    yellow: { bg: '#FEF3C7', text: '#B45309', border: '#FDE68A' },
    purple: { bg: '#F3E8FF', text: '#7E22CE', border: '#E9D5FF' },
    gray:   { bg: '#FFFFFF', text: '#64748B', border: '#E2E8F0' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
};

export const PlanBadge = ({ plan, billingCycle }) => {
  if (plan !== 'pro') return <Badge color="gray">Free</Badge>;
  const cycleLabel = billingCycle === 'annual' ? 'Yearly' : billingCycle === 'monthly' ? 'Monthly' : null;
  return <Badge color="purple">⭐ Pro{cycleLabel ? ` · ${cycleLabel}` : ''}</Badge>;
};

export const StatusBadge = ({ status }) => {
  const map = { active: 'green', suspended: 'red', pending: 'yellow' };
  return <Badge color={map[status] || 'gray'}>{status}</Badge>;
};

export const RoleBadge = ({ role }) => {
  const map = { superadmin: 'red', admin: 'purple', user: 'gray' };
  return <Badge color={map[role] || 'gray'}>{role}</Badge>;
};

export const Table = ({ headers, children, empty = 'No data found' }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={{ textAlign: 'left', padding: '10px 14px', color: '#64748B', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    {!children || (Array.isArray(children) && children.length === 0) ? (
      <div style={{ textAlign: 'center', padding: '32px', color: '#64748B', fontSize: 13 }}>{empty}</div>
    ) : null}
  </div>
);

export const TR = ({ children, onClick }) => (
  <tr onClick={onClick} style={{ borderBottom: '1px solid #E2E8F0', cursor: onClick ? 'pointer' : 'default', transition: 'background 0.1s' }}
    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
    {children}
  </tr>
);

export const TD = ({ children, style = {} }) => (
  <td style={{ padding: '11px 14px', color: '#334155', verticalAlign: 'middle', ...style }}>{children}</td>
);

export const Btn = ({ children, onClick, color = 'blue', size = 'md', disabled = false, style = {} }) => {
  const colors = {
    blue:   'background:linear-gradient(135deg,#EF1D28,#F97316);color:white',
    green:  'background:linear-gradient(135deg,#059669,#047857);color:white',
    red:    'background:linear-gradient(135deg,#DC2626,#B91C1C);color:white',
    yellow: 'background:linear-gradient(135deg,#D97706,#B45309);color:white',
    purple: 'background:linear-gradient(135deg,#7E22CE,#A855F7);color:white',
    ghost:  'background:#FFFFFF;color:#64748B;border:1px solid #E2E8F0',
    dark:   'background:#F8FAFC;color:#64748B;border:1px solid #E2E8F0',
  };
  const sizes = { sm: '5px 10px', md: '8px 16px', lg: '10px 22px' };
  const [bg, ...rest] = (colors[color] || colors.ghost).split(';');
  const bgStyle = Object.fromEntries([bg, ...rest].map(s => { const [k, v] = s.split(':'); return [k.trim(), v?.trim()]; }).filter(([k]) => k));
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...bgStyle, border: bgStyle.border || 'none', borderRadius: 7, padding: sizes[size], fontSize: size === 'sm' ? 11 : 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap', ...style }}>
      {children}
    </button>
  );
};

export const Input = ({ label, value, onChange, type = 'text', placeholder = '', style = {} }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>{label}</label>}
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, outline: 'none', boxSizing: 'border-box', ...style }} />
  </div>
);

export const Select = ({ label, value, onChange, options = [], style = {} }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>{label}</label>}
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, outline: 'none', boxSizing: 'border-box', cursor: 'pointer', ...style }}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export const Modal = ({ title, onClose, children, width = 480 }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
    <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 14, padding: 28, width: '100%', maxWidth: width, boxShadow: '0 20px 60px rgba(15,23,42,0.18)', maxHeight: '90vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, color: '#0F172A', fontSize: 16, fontWeight: 700 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

export const PageHeader = ({ title, subtitle, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
    <div>
      <h1 style={{ margin: '0 0 4px', color: '#0F172A', fontSize: 22, fontWeight: 800 }}>{title}</h1>
      {subtitle && <p style={{ margin: 0, color: '#64748B', fontSize: 13 }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Pagination = ({ page, pages, onChange }) => {
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', marginTop: 20 }}>
      <Btn color="ghost" size="sm" onClick={() => onChange(page - 1)} disabled={page === 1}>← Prev</Btn>
      <span style={{ color: '#64748B', fontSize: 12, padding: '0 8px' }}>Page {page} of {pages}</span>
      <Btn color="ghost" size="sm" onClick={() => onChange(page + 1)} disabled={page === pages}>Next →</Btn>
    </div>
  );
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
